import {
  Alert,
  Box,
  Drawer
} from "@mui/material";
import { useState } from "react";

import { useFilters } from "../../common/hooks/useFilters";
import FilterBar from "../../components/FilterBar";
import ProtocolPerformanceTable from "../../components/ProtocolPerformanceTable";
import { useProtocolPerformance } from "../../data/hooks/useProtocolPerformance";
import { CONVERSATION_LENS } from "../../utils/terminology";
import { getUserTimezone } from "../../utils/timezone";
import { useSelectedGroups } from "../auth/hooks/useSelectedGroups";
import { Viewer } from "../viewer/Viewer";
import DetailedProtocolView from "./components/DetailedProtocolView";

const Performance = () => {
  // Use the shared hook with performance-specific defaults
  const { filters, updateFilter } = useFilters({
    defaults: {
      processDecision: "DO_PROCESS",
      callType: "external"
    }
  });

  const timezone = getUserTimezone();
  const [selectedInteractionId, setSelectedInteractionId] = useState<string | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const { permittedEmails } = useSelectedGroups();
  const { data, isLoading, error } = useProtocolPerformance({
    timeRange: filters.timeRange,
    timezone,
    user: filters.user,
    permittedEmails
  });

  const handleProtocolClick = (key: string, value: string) => {
    // Map sc_name to the protocol filter name
    if (key === "sc_name") {
      updateFilter("protocol", value);
    } else {
      updateFilter(key as any, value);
    }
  };

  const [selectedAttribute, setSelectedAttribute] = useState<string | undefined>(undefined);

  const handleCallClick = (interactionId: string, attribute?: string) => {
    setSelectedInteractionId(interactionId);
    setSelectedAttribute(attribute);
    setIsViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setIsViewerOpen(false);
    setSelectedAttribute(undefined);
  };

  return (
    <Box sx={{ p: 3 }}>
      <FilterBar
        title="Performance Dashboard"
        subTitle={`Select a time period, user and ${CONVERSATION_LENS}`}
        timeRange={filters.timeRange}
        sentiment={filters.sentiment}
        relevance={filters.relevance}
        risk={filters.risk}
        protocol={filters.protocol}
        user={filters.user}
        processDecision={filters.processDecision}
        callType={filters.callType}
        onFilterChange={updateFilter}
        showFilters={{
          timeRange: true,
          protocol: true,
          user: true,
          // Only show these filters on this dashboard
          callType: false,
          sentiment: false,
          relevance: false,
          risk: false,
          processDecision: false
        }}
        onRefresh={() => {}}
        requiredFilters={{
          timeRange: true,
          protocol: true,
          user: true
        }}
      />

      {error && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      )}

      {filters.user === "all" ? (
        // Show message when only time range is selected (user is "all")
        <Box sx={{ mt: 2 }}>
          <Alert severity="info">Please select a user and {CONVERSATION_LENS}</Alert>
        </Box>
      ) : filters.protocol === "all" ? (
        // Show protocol performance table when user is selected but no protocol
        <Box sx={{ mt: 2 }}>
          <ProtocolPerformanceTable
            timeRange={filters.timeRange}
            timezone={timezone}
            user={filters.user}
            onClick={handleProtocolClick}
            isLoading={isLoading}
            data={data}
          />
        </Box>
      ) : (
        // Show detailed protocol view when both user and protocol are selected
        <DetailedProtocolView
          timeRange={filters.timeRange}
          timezone={timezone}
          protocol={filters.protocol}
          user={filters.user}
          onCallClick={handleCallClick}
        />
      )}

      {/* Call Viewer Drawer */}
      <Drawer
        anchor="right"
        open={isViewerOpen}
        onClose={handleCloseViewer}
        sx={{
          width: "75%",
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: "75%",
            boxSizing: "border-box",
            zIndex: (theme) => theme.zIndex.drawer + 2,
            transition: "transform 0.3s ease-in-out"
          }
        }}
        transitionDuration={300}
      >
        <Box sx={{ mt: 7 }}>
          {selectedInteractionId && (
            <Viewer
              interactionId={selectedInteractionId}
              initialTab={selectedAttribute ? 1 : 0}
              expandedAttribute={selectedAttribute}
              onClose={handleCloseViewer}
            />
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default Performance;
